import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { searchEvents, type EventItem } from '@/utils/news';

const navItems = [
  { label: '首页', to: '/' },
  { label: '赛事分类', to: '/category/all', hasMenu: true },
  { label: '关于我们', to: '/about' },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [suggestions, setSuggestions] = useState<EventItem[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
    setShowSuggest(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setSuggestions(searchEvents(value).slice(0, 6));
      setShowSuggest(true);
    } else {
      setSuggestions([]);
      setShowSuggest(false);
    }
  };

  const submitSearch = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setShowSuggest(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    if (to.startsWith('/category')) return location.pathname.startsWith('/category');
    return location.pathname === to;
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur-md border-b border-background-200">
      <div className="flex items-center gap-4 h-16 px-4 md:px-8 w-full">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl">
            <i className="ri-fire-line"></i>
          </span>
          <span className="font-heading font-extrabold text-lg md:text-xl tracking-tight text-foreground-950 whitespace-nowrap">
            每日大瓜<span className="text-primary-600">网红大赛</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((item) => (
            <div key={item.to} className="relative group/nav">
              <Link
                to={item.to}
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(item.to)
                    ? 'text-primary-600'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                {item.label}
                {item.hasMenu && <i className="ri-arrow-down-s-line text-base"></i>}
              </Link>
              {item.hasMenu && (
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all">
                  <div className="w-56 rounded-lg border border-background-200 bg-background-50 p-2 shadow-none">
                    <Link
                      to="/category/all"
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                    >
                      <i className="ri-apps-2-line text-accent-500"></i>
                      全部赛事
                    </Link>
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/category/${c.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                      >
                        <i className={`${c.icon} text-accent-500`}></i>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden md:block w-full max-w-sm ml-auto" ref={boxRef}>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => handleChange(e.target.value)}
              onFocus={() => keyword && setShowSuggest(true)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch(keyword)}
              placeholder="搜索赛事名称或关键词…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-background-300 bg-background-100 focus:bg-background-50 focus:border-accent-400 focus:outline-none text-foreground-800 placeholder:text-foreground-500"
            />
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 rounded-lg border border-background-200 bg-background-50 p-2 z-50">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => submitSearch(s.title)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-search-2-line text-foreground-400"></i>
                    <span className="line-clamp-1">{s.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto md:ml-3">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setMobileSearchOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-search-line text-xl"></i>
          </button>
          <a
            href="#footer"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-notification-3-line"></i>
            订阅更新
          </a>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch(keyword)}
              placeholder="搜索赛事名称或关键词…"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-100 focus:border-accent-400 focus:outline-none"
            />
          </div>
          {keyword && suggestions.length > 0 && (
            <div className="mt-2 rounded-lg border border-background-200 bg-background-50 p-2">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => submitSearch(s.title)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-search-2-line text-foreground-400"></i>
                  <span className="line-clamp-1">{s.title}</span>
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => submitSearch(keyword)}
            className="mt-2 w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer"
          >
            搜索
          </button>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`block px-3 py-2.5 rounded-md text-sm font-medium ${
                isActive(item.to) ? 'text-primary-600 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div className="mt-2 pt-2 border-t border-background-200">
            <p className="px-3 py-1 text-xs font-semibold text-foreground-500">赛事分类</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${c.icon} text-accent-500`}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}